import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  timestamp: number;
  autoClose?: boolean;
}

interface UIState {
  sidebarOpen: boolean;
  activeModal: string | null;
  notifications: Notification[];
  theme: 'light' | 'dark';
  isTyping: Record<string, boolean>; // conversationId -> isTyping
  connectionStatus: 'connected' | 'disconnected' | 'connecting';
  searchQuery: string;
  activeTab: string;
}

const initialState: UIState = {
  sidebarOpen: true,
  activeModal: null,
  notifications: [],
  theme: 'light',
  isTyping: {},
  connectionStatus: 'disconnected',
  searchQuery: '',
  activeTab: 'messages',
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setActiveModal: (state, action: PayloadAction<string | null>) => {
      state.activeModal = action.payload;
    },
    addNotification: (
      state,
      action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>
    ) => {
      const notification: Notification = {
        ...action.payload,
        id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        autoClose: action.payload.autoClose !== false, // Default to true
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },
    setTyping: (
      state,
      action: PayloadAction<{ conversationId: string; isTyping: boolean }>
    ) => {
      const { conversationId, isTyping } = action.payload;
      if (isTyping) {
        state.isTyping[conversationId] = true;
      } else {
        delete state.isTyping[conversationId];
      }
    },
    clearTyping: (state) => {
      state.isTyping = {};
    },
    setConnectionStatus: (
      state,
      action: PayloadAction<'connected' | 'disconnected' | 'connecting'>
    ) => {
      state.connectionStatus = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    resetUI: (state) => {
      state.activeModal = null;
      state.notifications = [];
      state.isTyping = {};
      state.searchQuery = '';
      state.connectionStatus = 'disconnected';
    },
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  setActiveModal,
  addNotification,
  removeNotification,
  clearNotifications,
  setTheme,
  setTyping,
  clearTyping,
  setConnectionStatus,
  setSearchQuery,
  setActiveTab,
  resetUI,
} = uiSlice.actions;

export default uiSlice.reducer;
